import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    WhatsappLogo,
    EnvelopeSimple,
    X,
    PaperPlaneRight,
    Sparkle,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "917582896874"; // intl. format, no +
const CONTACT_EMAIL =
    process.env.REACT_APP_CONTACT_EMAIL || "contact@idcardgen.com";

function readUtm() {
    if (typeof window === "undefined") return {};
    const sp = new URLSearchParams(window.location.search);
    return {
        utm_source: sp.get("utm_source") || undefined,
        utm_medium: sp.get("utm_medium") || undefined,
        utm_campaign: sp.get("utm_campaign") || undefined,
    };
}

export default function LeadWidgets() {
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        organization: "",
        mobile: "",
        email: "",
        message: "",
    });

    // Hide on the in-app studio editor where the toolbar overlaps
    const hidden = useMemo(
        () => loc.pathname.startsWith("/studio/"),
        [loc.pathname],
    );

    // Reset form whenever modal closes
    useEffect(() => {
        if (!open) {
            setForm({
                name: "",
                organization: "",
                mobile: "",
                email: "",
                message: "",
            });
        }
    }, [open]);

    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.name.trim()) return toast.error("Please enter your name");
        if (!form.mobile.trim() && !form.email.trim()) {
            return toast.error(
                "Add a mobile number or email so we can reach you",
            );
        }
        if (form.mobile && !/^\d{7,15}$/.test(form.mobile.replace(/\D/g, ""))) {
            return toast.error("Mobile number looks invalid");
        }
        setSubmitting(true);
        try {
            await api.post("/leads", {
                ...form,
                mobile: form.mobile.replace(/\D/g, "") || null,
                source: "demo_form",
                page_url: window.location.href,
                referrer: document.referrer || null,
                ...readUtm(),
            });
            toast.success("Thanks! We'll be in touch within a few hours.");
            setOpen(false);
            // Fire GA4 / Meta Pixel events if installed
            if (typeof window !== "undefined") {
                if (typeof window.gtag === "function") {
                    window.gtag("event", "generate_lead", {
                        method: "demo_form",
                    });
                }
                if (typeof window.fbq === "function") {
                    window.fbq("track", "Lead");
                }
            }
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) ||
                    err.response?.data?.error ||
                    err.message,
            );
        } finally {
            setSubmitting(false);
        }
    };

    const whatsappHref = useMemo(() => {
        const text = encodeURIComponent(
            "Hi IDCardGen team, I'd like to know more about your ID card generator.",
        );
        return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
    }, []);

    const trackWhatsApp = () => {
        // Fire-and-forget lead capture when user clicks WhatsApp
        api.post("/leads", {
            name: "WhatsApp click",
            mobile: null,
            email: null,
            source: "whatsapp_click",
            message: "User clicked WhatsApp floating button",
            page_url: window.location.href,
            referrer: document.referrer || null,
            ...readUtm(),
        }).catch(() => {});
        if (typeof window !== "undefined") {
            if (typeof window.gtag === "function") {
                window.gtag("event", "contact", { method: "whatsapp" });
            }
            if (typeof window.fbq === "function") {
                window.fbq("track", "Contact");
            }
        }
    };

    if (hidden) return null;

    return (
        <>
            {/* Floating action stack — pinned to RIGHT edge, vertically centered.
                Demo button is collapsed (icon-only square) by default and
                expands to reveal the label on hover with a smooth slide. */}
            <div
                className="fixed top-1/2 right-0 -translate-y-1/2 z-40 flex flex-col items-end gap-3 print:hidden"
                data-testid="lead-widgets"
            >
                <button
                    onClick={() => setOpen(true)}
                    className="group relative flex items-center bg-gradient-to-br from-zinc-900 to-zinc-800 text-white shadow-xl hover:shadow-2xl hover:from-violet-700 hover:to-fuchsia-700 overflow-hidden rounded-l-xl rounded-r-none transition-all duration-300 ease-out h-14 w-14 hover:w-52 pl-[18px] pr-0 hover:pr-4"
                    data-testid="open-demo-form-btn"
                    aria-label="Request a free demo"
                >
                    {/* Animated shimmer sweep on hover */}
                    <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                    <Sparkle
                        size={20}
                        weight="fill"
                        className="text-amber-300 shrink-0 animate-[pulse_2s_ease-in-out_infinite] group-hover:rotate-180 transition-transform duration-500"
                    />

                    {/* Label slides in on hover */}
                    <span className="relative ml-3 whitespace-nowrap text-sm font-semibold tracking-tight opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300 delay-75">
                        Request Demo
                    </span>

                    {/* FREE badge — visible only when collapsed, fades out on hover */}
                    <span className="absolute -top-1.5 -left-1.5 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-widest shadow-md group-hover:opacity-0 group-hover:scale-75 transition-all duration-200">
                        Free
                    </span>
                </button>

                <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackWhatsApp}
                    className="flex items-center justify-center w-14 h-14 rounded-l-full rounded-r-none bg-[#25D366] text-white shadow-xl hover:shadow-2xl hover:scale-110 hover:-translate-x-1 transition-all duration-300 relative"
                    data-testid="whatsapp-float-btn"
                    aria-label="Chat with us on WhatsApp"
                >
                    <WhatsappLogo size={28} weight="fill" />
                    <span className="absolute inset-0 rounded-l-full ring-4 ring-[#25D366]/30 animate-ping" />
                </a>
            </div>

            {/* Demo modal */}
            {open && (
                <div
                    className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4 backdrop-blur-sm"
                    data-testid="demo-modal"
                    onClick={() => setOpen(false)}
                >
                    <div
                        className="bg-white w-full max-w-lg border border-zinc-200 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between p-5 border-b border-zinc-200">
                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-violet-600 font-bold">
                                    ◆ Free Demo
                                </div>
                                <h2 className="font-display font-black text-2xl tracking-tighter mt-1">
                                    Talk to our team
                                </h2>
                                <p className="text-sm text-zinc-500 mt-1">
                                    We'll show you a live ID card export for
                                    your school in under 5 minutes.
                                </p>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="btn-ghost"
                                aria-label="Close"
                                data-testid="close-demo-modal"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-4">
                            <div>
                                <label className="label-swiss">Your Name</label>
                                <input
                                    className="input-swiss"
                                    value={form.name}
                                    onChange={setField("name")}
                                    required
                                    autoFocus
                                    data-testid="demo-name-input"
                                />
                            </div>
                            <div>
                                <label className="label-swiss">
                                    Organization / School
                                </label>
                                <input
                                    className="input-swiss"
                                    value={form.organization}
                                    onChange={setField("organization")}
                                    data-testid="demo-org-input"
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-swiss">
                                        Mobile (WhatsApp)
                                    </label>
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        maxLength={10}
                                        className="input-swiss"
                                        value={form.mobile}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                mobile: e.target.value
                                                    .replace(/\D/g, "")
                                                    .slice(0, 10),
                                            })
                                        }
                                        placeholder="10-digit"
                                        data-testid="demo-mobile-input"
                                    />
                                </div>
                                <div>
                                    <label className="label-swiss">Email</label>
                                    <input
                                        type="email"
                                        className="input-swiss"
                                        value={form.email}
                                        onChange={setField("email")}
                                        data-testid="demo-email-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="label-swiss">
                                    Message (optional)
                                </label>
                                <textarea
                                    rows={3}
                                    className="input-swiss"
                                    value={form.message}
                                    onChange={setField("message")}
                                    placeholder="How many cards per year? Any custom needs?"
                                    data-testid="demo-message-input"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-primary w-full flex items-center justify-center gap-2"
                                data-testid="demo-submit-btn"
                            >
                                <PaperPlaneRight size={16} weight="fill" />
                                {submitting ? "Sending…" : "Request Demo"}
                            </button>
                            <div className="text-xs text-zinc-500 text-center pt-2 border-t border-zinc-100 flex items-center justify-center gap-3 flex-wrap">
                                <a
                                    href={whatsappHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={trackWhatsApp}
                                    className="text-emerald-600 hover:underline inline-flex items-center gap-1 font-semibold"
                                >
                                    <WhatsappLogo size={14} weight="fill" />
                                    WhatsApp us
                                </a>
                                <span>·</span>
                                <a
                                    href={`mailto:${CONTACT_EMAIL}`}
                                    className="text-blue-600 hover:underline inline-flex items-center gap-1 font-semibold"
                                >
                                    <EnvelopeSimple size={14} weight="fill" />
                                    {CONTACT_EMAIL}
                                </a>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
